/** biome-ignore-all lint/suspicious/noExplicitAny: <Fix Type Later> */
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { CitrixNotification } from "@/components/citrix-notification/CitrixNotification";
import { useCitrixNotification } from "@/components/citrix-notification/useCitrixNotification";
import { RootState } from "@/lib/store/dynamicStore";
import { logoutUser } from "@/lib/store/features/auth/authSlice";
// import { useSearch } from "@/pages/search/hooks/useSearch";
import { canAccessAdmin } from "@/utils/roles";
import { HeaderNav } from "./header.model";

export interface IHeaderNav {
	title: string;
	path: string;
}

export function Header() {
	const navigate = useNavigate();
	const searchInputRef = useRef<HTMLInputElement>(null);
	const user = useSelector((state: RootState) => state.auth.user);
	const [toggleBurger, setToggleBurger] = useState(false);
	const [toggleSearchBar, _setToggleSearchBar] = useState(false);
	const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
	// const { searchQuery, setSearchQuery } = useSearch();
	const avatarMenuRef = useRef<HTMLDivElement>(null);
	const {
		isOpen: isCitrixNotificationOpen,
		setIsOpen: setIsCitrixNotificationOpen,
	} = useCitrixNotification([
		"/",
		"/methodologies",
		"/galileo",
		"/how-to",
		"/search/*",
		"*",
	]);
	const dispatch = useDispatch();
	const { pathname } = useLocation();

	// console.log(user);

	const handleClick = (route: string) => {
		navigate(route);
		setIsAvatarMenuOpen(false);
	};

	const getInitials = (name: string) => {
		return name
			.split(" ")
			.map((n) => n[0])
			.join("");
	};

	// const handleSearchSubmit = (e: React.FormEvent) => {
	// 	e.preventDefault();
	// 	if (searchQuery.trim()) {
	// 		navigate(`/search/${encodeURIComponent(searchQuery)}`);
	// 		setToggleSearchBar(false);
	// 		setSearchQuery("");
	// 		setIsAvatarMenuOpen(false);
	// 	}
	// };

	const handleLogout = () => {
		dispatch(logoutUser() as any);
		// Navigation will be handled by MSAL redirect
	};

	const handleAvatarClick = () => {
		setIsAvatarMenuOpen((prev) => !prev);
	};

	useEffect(() => {
		if (toggleSearchBar && searchInputRef.current) {
			searchInputRef.current.focus();
		}
	}, [toggleSearchBar]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				avatarMenuRef.current &&
				!avatarMenuRef.current.contains(event.target as Node)
			) {
				setIsAvatarMenuOpen(false);
			}
		};

		if (isAvatarMenuOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isAvatarMenuOpen]);

	return (
		<>
			<CitrixNotification
				isOpen={isCitrixNotificationOpen}
				onClose={() => setIsCitrixNotificationOpen(false)}
			/>
			<header
				className="flex items-center h-header w-full fixed top-0 left-0 z-[10] bg-grayscale-white mx-auto animate-headerSlide [animation-delay:300ms] [animation-duration:400ms]"
				style={{
					marginTop: isCitrixNotificationOpen ? "32px" : "0px",
					opacity: 0,
				}}
			>
				<div className="flex justify-between items-center max-w-[90%] px-40 w-full mx-auto">
					<div className="flex items-center gap-24">
						<a
							href="/"
							onClick={(e) => {
								e.preventDefault();
								handleClick("/");
							}}
							className="flex items-center gap-8 p-8 -m-8 text-[18px] font-semibold cursor-pointer transition-all duration-200 hover:translate-x-[6px] no-underline text-grayscale-950 hover:text-grayscale-950 rounded-24 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none"
						>
							<img
								className="w-[20px] h-[20px] bg-primary-600 rounded-full p-6"
								src="/logo.svg"
								alt=""
							/>
							Einstein
						</a>
						<div className="h-16 w-[1px] bg-grayscale-200"></div>
						<nav className="hidden md:flex items-center gap-8 -ml-[8px] font-rubik text-[14px] font-medium">
							{HeaderNav.map((navItem) => {
								// Debugging logs for active state
								const isRoot = navItem.path === "/";
								const isCurrentPathRoot = pathname === "/";
								const isRootMatch = isRoot && isCurrentPathRoot;
								const isSubPathMatch =
									!isRoot && pathname.startsWith(navItem.path);
								const isActive = isRootMatch || isSubPathMatch;

								// // console.log(
								//   `NavLink Check: Item Path="${navItem.path}", Current Path="${pathname}", isRootMatch=${isRootMatch}, isSubPathMatch=${isSubPathMatch}, isActive=${isActive}`
								// );

								return (
									<a
										key={navItem.path}
										href={navItem.path}
										onClick={(e) => {
											e.preventDefault();
											handleClick(navItem.path);
										}}
										className={clsx(
											"flex justify-center items-center gap-8 cursor-pointer text-center rounded-32 outline-none px-8 py-8 min-w-[100px] font-rubik font-medium text-[14px] transition-all duration-200 hover:-translate-y-[2px] select-none whitespace-nowrap no-underline focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none",
											isActive
												? "bg-primary-100 text-grayscale-900"
												: "bg-transparent text-grayscale-700 hover:bg-grayscale-50 hover:text-grayscale-700",
										)}
									>
										{navItem.title}
									</a>
								);
							})}
						</nav>
					</div>

					<div className="flex items-center gap-8">
						{/* Search form commented out
						<form
							onSubmit={handleSearchSubmit}
							className="relative flex items-center"
						>
							<img
								className="absolute left-12 top-1/2 -translate-y-1/2 w-[20px] h-[20px] z-[1] pointer-events-none"
								src="/icons/search.svg"
								alt=""
							/>
							<input
								className="pl-40 pr-12 py-6 w-0 text-[14px] font-rubik bg-grayscale-50 border border-grayscale-950 rounded-24 outline-none transition-all duration-100 focus:outline-none focus:ring-1 focus:ring-grayscale-950 focus:border-transparent animate-headerSearch"
								ref={searchInputRef}
								type="text"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								placeholder="Search Citrix and Sharepoint"
							/>
							<button type="submit" hidden />
						</form>
						*/}
						<div ref={avatarMenuRef} className="relative">
							<button
								onClick={handleAvatarClick}
								aria-label="User menu"
								aria-expanded={isAvatarMenuOpen}
								aria-haspopup="true"
								className="flex justify-center items-center w-[32px] h-[32px] border-2 border-primary-500 rounded-full transition-all duration-200 hover:bg-primary-500 group cursor-pointer focus-visible:outline-2 focus-visible:outline-primary-300 focus-visible:outline-offset-2"
							>
								<span className="relative top-[1px] text-[12px] text-primary-700 font-semibold select-none transition-all duration-200 group-hover:text-grayscale-white">
									{getInitials(user?.name || "")}
								</span>
							</button>
							{isAvatarMenuOpen && (
								<div
									role="menu"
									aria-orientation="vertical"
									aria-labelledby="user-menu-button"
									className="absolute top-[calc(100%+8px)] right-0 min-w-[150px] bg-grayscale-white border border-grayscale-200 rounded-24 shadow-lg p-[12px] z-[20] flex flex-col gap-1 animate-fadeIn"
								>
									{canAccessAdmin(user) && (
										<button
											role="menuitem"
											className="text-left flex items-center justify-start px-12 py-8 w-full text-grayscale-700 font-rubik text-[14px] font-normal bg-transparent rounded-4 transition-colors duration-200 hover:bg-grayscale-50 hover:text-grayscale-900 focus:bg-grayscale-50 focus:text-grayscale-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-inset"
											onClick={() => handleClick("/admin")}
										>
											Admin
										</button>
									)}
									<button
										role="menuitem"
										className="text-left flex items-center justify-start px-12 py-8 w-full font-rubik text-[14px] font-normal text-system-red-600 rounded-4 transition-colors duration-200 hover:bg-system-red-50 hover:text-system-red-700 focus:bg-system-red-50 focus:text-system-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-system-red-400 focus-visible:ring-inset"
										onClick={handleLogout}
									>
										Logout
									</button>
								</div>
							)}
						</div>

						<div
							className="flex flex-col justify-between h-16 rounded-4 p-4 ml-12 bg-transparent border-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none md:hidden"
							onClick={() => setToggleBurger(!toggleBurger)}
						>
							{[0, 1, 2].map((item) => (
								<div
									key={item}
									className={clsx(
										"w-[20px] h-[2px] bg-grayscale-950 transition-all duration-200",
										toggleBurger && {
											"first:rotate-[-45deg] first:translate-x-[-7px] first:translate-y-[3px]":
												item === 0,
											"opacity-0": item === 1,
											"last:rotate-[45deg] last:translate-x-[-7px] last:translate-y-[-3px]":
												item === 2,
										},
									)}
								></div>
							))}
						</div>
					</div>
				</div>

				{toggleBurger && (
					<div className="fixed z-[30] top-header h-[calc(100dvh-64px)] w-full bg-grayscale-white">
						<nav className="flex flex-col gap-16 p-24 px-40 pt-24">
							{HeaderNav.map((navItem, index) => (
								<a
									key={navItem.path}
									href={navItem.path}
									className="min-w-0 w-max p-8 px-0 opacity-0 origin-top animate-rotateX text-left font-rubik text-[16px] font-normal text-grayscale-700 hover:text-primary-500 transition-colors duration-200 no-underline rounded-4 focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:outline-none focus-visible:bg-grayscale-50"
									style={{
										animationDelay: `${200 + index * 90}ms`,
										animationFillMode: "forwards",
									}}
									onClick={(e) => {
										e.preventDefault();
										handleClick(navItem.path);
										setToggleBurger(false);
									}}
								>
									{navItem.title}
								</a>
							))}
						</nav>
					</div>
				)}
			</header>
		</>
	);
}
